/**
 * Renata Mini PWA — Layout shell at /renata.
 * Standalone mobile-first shell (NOT full ERP).
 * Auth: requires logged-in user with tenant access; redirects to login with returnTo.
 */
import React from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { requireSessionClient } from "@/lib/services/auth-guard";
import { useEffect } from "react";
import { Bot, MessageSquare, History, Settings, Download, Flag, Zap, Target, CheckCircle2, Bell, BookOpen, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { getStoredTenantId } from "@/lib/sm-api";

export const Route = createFileRoute("/renata")({
  beforeLoad: async ({ location }) => {
    const session = await requireSessionClient();
    if (!session) {
      throw redirect({
        to: "/acceso/comercio",
        search: { returnTo: location.pathname, error: undefined },
      });
    }
    const tid = getStoredTenantId();
    if (!tid) {
      throw redirect({ to: "/app/dashboard" });
    }
  },
  head: () => ({
    meta: [
      { title: "Renata" },
      { name: "theme-color", content: "#7c3aed" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Renata" },
    ],
    links: [
      { rel: "manifest", href: "/api/renata/manifest" },
    ],
  }),
  component: RenataMiniLayout,
});

const NAV_TABS: Array<{ to: string; label: string; icon: React.ElementType; exact?: boolean }> = [
  { to: "/renata", label: "Chat", icon: MessageSquare, exact: true },
  { to: "/renata/os", label: "OS", icon: Target },
  { to: "/renata/historial", label: "Historial", icon: History },
  { to: "/renata/config", label: "Config", icon: Settings },
];

function RenataMiniLayout() {
  const location = useLocation();
  const { user: session } = useSession();

  useEffect(() => {
    if (typeof window === "undefined") return;
    document.documentElement.style.setProperty("--renata-mini", "1");
    return () => {
      document.documentElement.style.removeProperty("--renata-mini");
    };
  }, []);

  if (!session) return null;

  return (
    <div className="flex h-dvh flex-col bg-background antialiased">
      {/* Top bar */}
      <header className="flex shrink-0 items-center gap-3 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
          <Bot className="h-4 w-4" />
        </div>
        <span className="text-base font-semibold tracking-tight">Renata</span>
        <div className="ml-auto">
          <PwaInstallHint />
        </div>
      </header>

      {/* Main content */}
      <main className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav className="shrink-0 border-t bg-background pb-safe">
        <div className="flex">
          {NAV_TABS.map((tab) => {
            const isActive = tab.exact
              ? location.pathname === tab.to || location.pathname === tab.to.replace(/\/$/, "")
              : location.pathname.startsWith(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to as never}
                search={{} as never}
                className={cn(
                  "flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors",
                  isActive
                    ? "text-violet-600 dark:text-violet-400"
                    : "text-muted-foreground",
                )}
              >
                <tab.icon
                  className={cn(
                    "h-5 w-5",
                    isActive ? "text-violet-600 dark:text-violet-400" : "text-muted-foreground",
                  )}
                />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function PwaInstallHint() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      (window as Window & { __renataDeferredInstall?: Event }).__renataDeferredInstall = e;
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function tryInstall() {
    const deferredPrompt = (window as Window & { __renataDeferredInstall?: { prompt: () => void } }).__renataDeferredInstall;
    if (deferredPrompt) {
      deferredPrompt.prompt();
    }
  }

  if (typeof window === "undefined") return null;
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone;
  if (isStandalone) return null;

  return (
    <button
      onClick={tryInstall}
      className="flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300"
      type="button"
    >
      <Download className="h-3 w-3" />
      Instalar
    </button>
  );
}
